import { action, makeObservable } from "mobx";
import { AxiosError } from "axios";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { STA_getConfigureUrl } from "General/APIQueryParams";
import { CoreStore } from "Stores/RESTStore/CoreStore";
import { BrowserStorage } from "utils/BrowserStorage";
import { notificationError } from "utils/libHelpers/AntdNotification";
import i18n from "i18next";
import { I18N_NS } from "Config/i18n";

const STA_STALE_LINK_CODE = 1073;
const STA_RETURN_PATH = "sta-return-path";

const storageKey = (prefix: string, projectId: number | string): string => `${prefix}-${projectId}`;

const buildStaCallbackUrl = (projectId: number | string): string =>
  `${window.location.origin}/${window.GLOBAL.API_BASE}/project/${projectId}/sta/callback`;

const openStaLoginInNewTab = (url: string): void => {
  window.open(url, "_blank");
};

const openStaConfigureInSameTab = (url: string): void => {
  window.location.assign(url);
};

const isStaStaleLinkError = (error: unknown): boolean => {
  if (!(error instanceof AxiosError) || error.response?.status !== 400) {
    return false;
  }
  const data = error.response.data as { code?: number } | undefined;
  return data?.code === STA_STALE_LINK_CODE;
};

class StaIntegrationStore extends CoreStore {
  private openInProgress = false;

  constructor() {
    super();

    makeObservable(this, {
      openSta: action.bound,
    });
  }

  clear() {
    this.openInProgress = false;
  }

  url(projectId: number | string): string {
    return API_URL.STA.getUserLinkStatus(projectId);
  }

  async openSta(projectId: number) {
    if (this.openInProgress) {
      return;
    }

    this.openInProgress = true;
    const t = (key: string) => i18n.t(key, { ns: I18N_NS.PROJECT });

    try {
      const linkStatus = await API.getData<API_StaUserLinkStatus>(API_URL.STA.getUserLinkStatus(projectId));

      if (linkStatus.linked) {
        try {
          openStaLoginInNewTab(await this.fetchLoginUrl(projectId));
          return;
        } catch (error) {
          if (isStaStaleLinkError(error)) {
            await this.startConfigureFlow(projectId);
            return;
          }
          throw error;
        }
      }

      await this.startConfigureFlow(projectId);
    } catch (error) {
      notificationError(t("Error"), t("STA integration is currently unavailable. Please try again later."));
      throw error;
    } finally {
      this.openInProgress = false;
    }
  }

  async startConfigureFlow(projectId: number) {
    this.saveReturnPath(projectId);

    const configureParams: STA_getConfigureUrl = {
      redirectUrl: buildStaCallbackUrl(projectId),
    };
    const { configureUrl } = await API.getData<API_StaConfigureUrlResponse>(
      API_URL.STA.getConfigureUrl(projectId),
      configureParams
    );
    openStaConfigureInSameTab(configureUrl);
  }

  async saveUserLink(projectId: number, companyCode: string, userGuid: string) {
    const request: API_StaUserLinkRequest = { companyCode, userGuid };
    return API.newData<API_StaUserLinkStatus>(API_URL.STA.saveUserLink(projectId), request);
  }

  finishLinkCallback(projectId: number | string, navigate: (path: string) => void) {
    navigate(this.consumeReturnPath(projectId));
  }

  private saveReturnPath(projectId: number | string) {
    const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    BrowserStorage.store(storageKey(STA_RETURN_PATH, projectId), returnPath);
  }

  private consumeReturnPath(projectId: number | string): string {
    const key = storageKey(STA_RETURN_PATH, projectId);
    const storedPath = BrowserStorage.store(key) as string | undefined;
    BrowserStorage.removeStored(key);
    const returnPath = storedPath || `/project/${projectId}/overview`;
    const routerBase = window.GLOBAL?.ROUTER_BASE;

    if (routerBase && returnPath.startsWith(`/${routerBase}`)) {
      return returnPath.slice(routerBase.length + 1) || "/";
    }

    return returnPath;
  }

  private async fetchLoginUrl(projectId: number): Promise<string> {
    const { loginUrl } = await API.getData<API_StaLoginUrlResponse>(API_URL.STA.getLoginUrl(projectId));
    return loginUrl;
  }
}

export const staIntegrationStore = new StaIntegrationStore();
